import { useEffect, useRef, useState, ImgHTMLAttributes } from "react";
import { cn } from "@/lib/utils";
import { optimizedImageUrl, optimizedSrcSet } from "@/lib/mediaCdn";

interface LazyImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "loading"> {
  src?: string;
  rootMargin?: string;
  /** Disable URL optimization (pass the original src untouched). */
  raw?: boolean;
  /** Quality 1-100, default 75. */
  quality?: number;
  /** Skip srcset generation when sizing is fully controlled by parent. */
  noSrcSet?: boolean;
}

/**
 * IntersectionObserver-based lazy image with automatic CDN optimization.
 * - Defers the network request until ~200px from the viewport.
 * - Rewrites remote URLs to serve WebP at the right size.
 * - Emits a responsive srcset so the browser picks the best variant.
 */
const LazyImage = ({
  src,
  className,
  rootMargin = "200px",
  alt = "",
  raw = false,
  quality,
  noSrcSet = false,
  sizes,
  ...rest
}: LazyImageProps) => {
  const ref = useRef<HTMLImageElement>(null);
  const [visible, setVisible] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true);
            obs.disconnect();
            break;
          }
        }
      },
      { rootMargin, threshold: 0.01 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [visible, rootMargin]);

  const finalSrc = !visible
    ? undefined
    : raw || !src
    ? src
    : optimizedImageUrl(src, { quality });

  const finalSrcSet =
    !visible || raw || noSrcSet || !src ? undefined : optimizedSrcSet(src);

  return (
    <img
      ref={ref}
      src={finalSrc}
      srcSet={finalSrcSet}
      sizes={sizes ?? (finalSrcSet ? "100vw" : undefined)}
      alt={alt}
      decoding="async"
      onLoad={() => setLoaded(true)}
      className={cn(
        "transition-opacity duration-300",
        !loaded && "opacity-0 bg-muted animate-pulse",
        loaded && "opacity-100",
        className
      )}
      {...rest}
    />
  );
};

export default LazyImage;
